import {
  useContext, useEffect, useState, useMemo, useCallback,
} from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from '@mui/material';
import { useHistory } from 'react-router';
import { match } from 'ts-pattern';
import { useGetTheme } from '../../../hooks/use-get-theme';
import OperationButtonContext from '../operation-button-context';
import { useGetRouteToIssuerPage } from '../../../app/get-route-to-page';
import { Page } from '../../../app/page-url-path';
import { getDataBasedOnEnrollmentStatus } from '../../../dotcms/dotcms-models/model-based-on-enrollment-status-helper';
import { useEnrollmentPeriodOpenInfo } from '../../../hooks/use-get-periods-info/use-enrollment-period/use-enrollment-period-open-info';
import { useGetUserStatesInEnrollment } from '../../../hooks/use-get-user-states-in-enrollment';
import { authSelector, userSelector } from '../../../selectors';
import { PotentialOfferingPeriodAction } from '../../../selectors/interfaces';
import { usePostWithdrawUndo } from '../../../hooks/API/use-post-withdraw-undo';
import { useGetContributionChangesInfo } from '../../abc/account/account-page-manage-election/change-contribution-section-module/hooks/use-get-contribution-changes-info';
import { getRelativeStringWithSearchParam } from '../../../utils/url-search-params/set-url-search-param-on-path';
import UrlSearchParamKey, {
  ElectionUpdateTypeSearchParamValue,
} from '../../../utils/url-search-params/url-search-params';
import { useThemeToken } from '../../../hooks/use-get-theme/use-get-theme';

const getEnrollmentUrlWithParamToEditCurrent = (
  relativeRouteToEnrollment: string,
) =>
  // Set the URL param that tells the enrollment page it's editing elections
  // instead of a new enrollment:
  getRelativeStringWithSearchParam(
    relativeRouteToEnrollment,
    UrlSearchParamKey.EDITING_ELECTION,
    ElectionUpdateTypeSearchParamValue.EDITING_CURRENT_PURCHASE_PERIOD_ELECTIONS,
  );

export enum OperationButtonAction {
  UNDO_FUTURE_WITHDRAW = 'UNDO_FUTURE_WITHDRAW',
  ENROLL = 'ENROLL',
  ADD_CASHLESS_PARTICIPATION = 'ADD_CASHLESS_PARTICIPATION',
  SCHEDULE_ENROLLMENT = 'SCHEDULE_ENROLLMENT',
}

const useOperationButton = () => {
  const theme = useGetTheme();
  const muiTheme = useTheme();
  const themeToken = useThemeToken();

  const { data, loading, error } = useContext(OperationButtonContext);
  const userStates = useGetUserStatesInEnrollment();
  const { isEnrollmentOrScheduledEnrollmentOpen, isScheduledEnrollmentOpen } =
    useEnrollmentPeriodOpenInfo();
  const history = useHistory();
  const routeToEnrollmentPage = useGetRouteToIssuerPage(Page.ENROLLMENT_PAGE);
  const { isAuthenticated } = useSelector(authSelector);
  const { currentOpAllowedActions, futureOpAllowedActions } =
    useSelector(userSelector);

  const { isInResetChangeWindow } = useGetContributionChangesInfo();

  const allAvailableActions = useMemo(
    () => [
      ...(currentOpAllowedActions ?? []),
      ...(futureOpAllowedActions ?? []),
    ],
    [currentOpAllowedActions, futureOpAllowedActions],
  );

  const label = useMemo(
    () =>
      (data
        ? getDataBasedOnEnrollmentStatus(
          {
            defaultData: undefined,
            ifEnrollmentIsAvailableData: data.enrollNowLabel,
            ifInEsppButNotInCashless: data.addCashlessParticipationLabel,
            ifReadyToAddCashlessMatch: data.cashlessMatchLabel,
          },
          userStates,
          isEnrollmentOrScheduledEnrollmentOpen,
        )
        : undefined),
    [data, isEnrollmentOrScheduledEnrollmentOpen, userStates],
  );

  const action = useMemo(() => {
    if (!label) {
      return undefined;
    }

    return match({
      label,
    })
      .with(
        { label: data?.enrollNowLabel },
        { label: data?.cashlessMatchLabel },
        () => {
          // UndoFutureWithdraw is always in future actions
          // regardless of the purchase or offering period
          if (
            futureOpAllowedActions?.includes(
              PotentialOfferingPeriodAction.UndoFutureWithdraw,
            )
          ) {
            return OperationButtonAction.UNDO_FUTURE_WITHDRAW;
          }

          if (isScheduledEnrollmentOpen) {
            return OperationButtonAction.SCHEDULE_ENROLLMENT;
          }

          return !isAuthenticated ||
            allAvailableActions.includes(
              PotentialOfferingPeriodAction.EnrollElectionAction,
            )
            ? OperationButtonAction.ENROLL
            : undefined;
        },
      )
      .with({ label: data?.addCashlessParticipationLabel }, () =>
        ([
          PotentialOfferingPeriodAction.UpdateElectionAction,
          PotentialOfferingPeriodAction.EnrollElectionAction,
          PotentialOfferingPeriodAction.ScheduledEnrollmentAction,
        ].find((x) => allAvailableActions.includes(x))
          ? OperationButtonAction.ADD_CASHLESS_PARTICIPATION
          : undefined))
      .otherwise(() => undefined);
  }, [
    data?.enrollNowLabel,
    data?.addCashlessParticipationLabel,
    data?.cashlessMatchLabel,
    isAuthenticated,
    allAvailableActions,
    futureOpAllowedActions,
    isScheduledEnrollmentOpen,
    label,
  ]);

  const {
    data: postWithdrawUndoData,
    error: postWithdrawUndoError,
    loading: postWithdrawUndoLoading,
    sendRequest: postWithdrawUndo,
  } = usePostWithdrawUndo();

  const [withdrawalUndoDialogOpen, setWithdrawalUndoDialogOpen] =
    useState(false);

  const handleWithdrawalUndoDialogUndo = useCallback(() => {
    postWithdrawUndo(true);
  }, [postWithdrawUndo]);

  const handleWithdrawalUndoDialogClose = useCallback(() => {
    setWithdrawalUndoDialogOpen(false);
  }, [setWithdrawalUndoDialogOpen]);

  useEffect(() => {
    if (
      postWithdrawUndoData &&
      !postWithdrawUndoLoading &&
      !postWithdrawUndoError
    ) {
      setWithdrawalUndoDialogOpen(false);
    }
  }, [postWithdrawUndoData, postWithdrawUndoLoading, postWithdrawUndoError]);

  const routeToEnrollmentPageToChangeCurrent =
    getEnrollmentUrlWithParamToEditCurrent(routeToEnrollmentPage);

  const handlePressButton = useCallback(
    () =>
      match({ actionType: action })
        .with({ actionType: OperationButtonAction.UNDO_FUTURE_WITHDRAW }, () =>
          setWithdrawalUndoDialogOpen(true))
        .with({ actionType: undefined }, () => {})
        .otherwise(() =>
          history.push(
            isInResetChangeWindow
              ? routeToEnrollmentPageToChangeCurrent
              : routeToEnrollmentPage,
          )),
    [
      action,
      history,
      isInResetChangeWindow,
      routeToEnrollmentPage,
      routeToEnrollmentPageToChangeCurrent,
    ],
  );

  return {
    theme,
    muiTheme,
    themeToken,
    action,
    defaultLabel: data?.enrollNowLabel,
    label:
      action === OperationButtonAction.SCHEDULE_ENROLLMENT
        ? data?.scheduleEnrollmentLabel
        : label,
    handlePressButton,
    stayEnrolledDialog: data?.stayEnrolledDialog,
    loading,
    error,
    withdrawalUndoDialogOpen,
    handleWithdrawalUndoDialogUndo,
    handleWithdrawalUndoDialogClose,
    postWithdrawUndoLoading,
  };
};

export default useOperationButton;
